import React, { useEffect, useState } from 'react';
import { collection, doc, onSnapshot, updateDoc, deleteDoc, addDoc, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../AuthContext';

export const VaultMatrix = () => {
  const { user } = useAuth();
  
  // State for data
  const [rotations, setRotations] = useState([]);
  const [roster, setRoster] = useState([]);
  const [honeyDos, setHoneyDos] = useState([]);
  const [historyItems, setHistoryItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // State for inline additions
  const [newRotation, setNewRotation] = useState({ title: '', description: '' });
  const [newHoneyDo, setNewHoneyDo] = useState({ title: '', description: '' });
  const [newHistory, setNewHistory] = useState({ title: '', content: '' });

  // State for editing inline
  const [editingId, setEditingId] = useState(null);
  const [editFields, setEditFields] = useState({});

  useEffect(() => {
    if (!user) return;

    const rotationsRef = collection(db, `users/${user.uid}/vaultRotations`);
    const unsubRotations = onSnapshot(rotationsRef, (snapshot) => {
      setRotations(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    const honeyDosRef = collection(db, `users/${user.uid}/vaultHoneyDos`);
    const unsubHoneyDos = onSnapshot(honeyDosRef, (snapshot) => {
      setHoneyDos(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    const historyRef = collection(db, `users/${user.uid}/vaultHistory`);
    const unsubHistory = onSnapshot(historyRef, (snapshot) => {
      setHistoryItems(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });

    const rosterRef = doc(db, `users/${user.uid}/settings`, 'vaultRosterData');
    const unsubRoster = onSnapshot(rosterRef, (snapshot) => {
      if (snapshot.exists()) setRoster(snapshot.data().roster || []);
    });

    return () => {
      unsubRotations();
      unsubHoneyDos();
      unsubHistory();
      unsubRoster();
    };
  }, [user]);

  // --- CRUD ACTIONS ---

  const handleAddItem = async (collectionName, data, resetState) => {
    if (!data.title.trim()) return;
    await addDoc(collection(db, `users/${user.uid}/${collectionName}`), {
      ...data,
      createdAt: new Date().toISOString()
    });
    resetState();
  };

  const handleDeleteItem = async (collectionName, id) => {
    if (window.confirm("Are you sure you want to remove this item?")) {
      await deleteDoc(doc(db, `users/${user.uid}/${collectionName}`, id));
    }
  };

  const startEditing = (item, initialFields) => {
    setEditingId(item.id);
    setEditFields({ title: item.title, ...initialFields });
  };

  const handleSaveEdit = async (collectionName, id) => {
    await updateDoc(doc(db, `users/${user.uid}/${collectionName}`, id), editFields);
    setEditingId(null);
  };

  // --- ROTATION CALCULATION ---
  const getAssignment = (taskIndex, rosterList) => {
    if (!rosterList || rosterList.length === 0) return { current: '—' };
    const localDateStr = new Date().toLocaleDateString('sv-SE');
    const startDate = new Date('2024-01-01');
    const todayDate = new Date(localDateStr);
    const diffDays = Math.floor(Math.abs(todayDate - startDate) / (1000 * 60 * 60 * 24));
    const currentIdx = (diffDays + taskIndex) % rosterList.length;
    return { current: rosterList[currentIdx] };
  };

  const handlePassToken = async () => {
    if (roster.length < 2) return;
    const newRoster = [...roster];
    const shifted = newRoster.shift();
    newRoster.push(shifted);
    await updateDoc(doc(db, `users/${user.uid}/settings`, 'vaultRosterData'), { roster: newRoster });
  };

  if (loading) return <div className="p-10 text-center text-gray-500">Loading vault matrix...</div>;

  return (
    <div className="max-w-7xl mx-auto p-4">
      <header className="flex justify-between items-center mb-8 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
        <div>
          <h2 className="text-3xl font-bold text-gray-800 tracking-tight">The Domestic Vault</h2>
          <p className="text-gray-400 font-bold text-[10px] md:text-xs uppercase tracking-widest mt-1">
            Operational Matrix & Family Archives
          </p>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* COLUMN 1: ROUTINE ROTATIONS */}
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden flex flex-col transition-all hover:shadow-md">
          <div className="bg-gradient-to-br from-indigo-50 to-blue-50 p-6 border-b border-gray-100">
            <h3 className="text-2xl font-black text-gray-800">Routine Rotations</h3>
          </div>

          <div className="p-6 bg-white flex flex-col flex-grow">
            {/* Inline Add Form */}
            <div className="mb-6 p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
              <input 
                type="text" placeholder="Add rotation name..." value={newRotation.title}
                onChange={e => setNewRotation({...newRotation, title: e.target.value})}
                className="w-full text-xs p-2 rounded-lg border border-gray-200 focus:outline-indigo-500"
              />
              <input 
                type="text" placeholder="Description..." value={newRotation.description}
                onChange={e => setNewRotation({...newRotation, description: e.target.value})}
                className="w-full text-xs p-2 rounded-lg border border-gray-200 focus:outline-indigo-500"
              />
              <button 
                onClick={() => handleAddItem('vaultRotations', newRotation, () => setNewRotation({title:'', description:''}))}
                className="w-full text-[11px] bg-indigo-600 text-white font-bold py-1.5 rounded-lg hover:bg-indigo-700 transition"
              >
                + Add Rotation
              </button>
            </div>

            <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-4">Active Rotations</h4>
            <div className="space-y-4 max-h-[450px] overflow-y-auto pr-1">
              {rotations.map((rot, idx) => {
                const { current } = getAssignment(idx, roster);
                const isEditing = editingId === rot.id;

                return (
                  <div key={rot.id} className="p-4 bg-blue-50/50 rounded-xl border border-blue-100 relative group">
                    {isEditing ? (
                      <div className="space-y-2">
                        <input type="text" value={editFields.title} onChange={e => setEditFields({...editFields, title: e.target.value})} className="w-full text-xs p-1.5 rounded border" />
                        <input type="text" value={editFields.description} onChange={e => setEditFields({...editFields, description: e.target.value})} className="w-full text-xs p-1.5 rounded border" />
                        <div className="flex gap-2">
                          <button onClick={() => handleSaveEdit('vaultRotations', rot.id)} className="text-[10px] bg-green-600 text-white px-2 py-1 rounded font-bold">Save</button>
                          <button onClick={() => setEditingId(null)} className="text-[10px] bg-gray-400 text-white px-2 py-1 rounded font-bold">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 bg-white/80 backdrop-blur-sm p-1 rounded-lg border">
                          <button onClick={() => startEditing(rot, { description: rot.description })} className="text-[10px] text-gray-500 hover:text-blue-600 px-1">Edit</button>
                          <button onClick={() => handleDeleteItem('vaultRotations', rot.id)} className="text-[10px] text-gray-500 hover:text-red-600 px-1">Delete</button>
                        </div>
                        <p className="font-bold text-blue-900 text-sm leading-tight pr-12">{rot.title}</p>
                        <p className="text-xs text-slate-500 mt-1 leading-relaxed">{rot.description}</p>
                        <div className="mt-4 pt-3 border-t border-blue-100 flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            <div className="w-5 h-5 rounded-md bg-indigo-600 text-white text-[10px] font-black flex items-center justify-center shadow-sm uppercase">
                              {current[0]}
                            </div>
                            <span className="text-xs font-bold text-blue-900">{current}'s Turn</span>
                          </div>
                          <button onClick={handlePassToken} className="text-[10px] bg-white border border-blue-200 text-blue-700 hover:bg-blue-100 px-2.5 py-1.5 rounded-lg font-bold shadow-sm transition">
                            Pass Token →
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* COLUMN 2: THE HONEY-DO QUEUE */}
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden flex flex-col transition-all hover:shadow-md">
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-6 border-b border-gray-100">
            <h3 className="text-2xl font-black text-gray-800">The Honey-Do Queue</h3>
          </div>

          <div className="p-6 bg-white flex flex-col flex-grow">
            {/* Inline Add Form */}
            <div className="mb-6 p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
              <input 
                type="text" placeholder="Add backlog task..." value={newHoneyDo.title}
                onChange={e => setNewHoneyDo({...newHoneyDo, title: e.target.value})}
                className="w-full text-xs p-2 rounded-lg border border-gray-200 focus:outline-amber-500"
              />
              <input 
                type="text" placeholder="Task details..." value={newHoneyDo.description}
                onChange={e => setNewHoneyDo({...newHoneyDo, description: e.target.value})}
                className="w-full text-xs p-2 rounded-lg border border-gray-200 focus:outline-amber-500"
              />
              <button 
                onClick={() => handleAddItem('vaultHoneyDos', { ...newHoneyDo, nagCount: 0, status: 'pending' }, () => setNewHoneyDo({title:'', description:''}))}
                className="w-full text-[11px] bg-amber-600 text-white font-bold py-1.5 rounded-lg hover:bg-amber-700 transition"
              >
                + Add Task
              </button>
            </div>

            <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-4">Pending Backlog</h4>
            <div className="space-y-4 max-h-[450px] overflow-y-auto pr-1">
              {honeyDos.map((todo) => {
                const isEditing = editingId === todo.id;
                return (
                  <div key={todo.id} className="p-4 bg-amber-50/30 rounded-xl border border-amber-100 relative group">
                    {isEditing ? (
                      <div className="space-y-2">
                        <input type="text" value={editFields.title} onChange={e => setEditFields({...editFields, title: e.target.value})} className="w-full text-xs p-1.5 rounded border" />
                        <input type="text" value={editFields.description} onChange={e => setEditFields({...editFields, description: e.target.value})} className="w-full text-xs p-1.5 rounded border" />
                        <div className="flex gap-2">
                          <button onClick={() => handleSaveEdit('vaultHoneyDos', todo.id)} className="text-[10px] bg-green-600 text-white px-2 py-1 rounded font-bold">Save</button>
                          <button onClick={() => setEditingId(null)} className="text-[10px] bg-gray-400 text-white px-2 py-1 rounded font-bold">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 bg-white/80 backdrop-blur-sm p-1 rounded-lg border">
                          <button onClick={() => startEditing(todo, { description: todo.description })} className="text-[10px] text-gray-500 hover:text-blue-600 px-1">Edit</button>
                          <button onClick={() => handleDeleteItem('vaultHoneyDos', todo.id)} className="text-[10px] text-gray-500 hover:text-red-600 px-1">Delete</button>
                        </div>
                        <p className="font-bold text-amber-900 text-sm leading-tight pr-12">{todo.title}</p>
                        <p className="text-xs text-slate-600 mt-1 leading-relaxed">{todo.description}</p>
                        <div className="mt-4 pt-3 border-t border-amber-100 flex justify-between items-center text-xs">
                          <span className="text-[10px] font-bold text-amber-600 flex items-center gap-1.5 uppercase tracking-wider">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
                            Bot Active
                          </span>
                          <span className="text-[10px] font-mono bg-amber-100 text-amber-800 px-2 py-0.5 rounded font-bold">
                            {todo.nagCount || 0} Nags
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* COLUMN 3: THE HISTORICAL VAULT */}
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden flex flex-col transition-all hover:shadow-md">
          <div className="bg-gradient-to-br from-gray-50 to-slate-100 p-6 border-b border-gray-100">
            <h3 className="text-2xl font-black text-gray-800">Historical Vault</h3>
          </div>

          <div className="p-6 bg-white flex flex-col flex-grow">
            {/* Inline Add Form */}
            <div className="mb-6 p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
              <input 
                type="text" placeholder="Add list or archive title..." value={newHistory.title}
                onChange={e => setNewHistory({...newHistory, title: e.target.value})}
                className="w-full text-xs p-2 rounded-lg border border-gray-200 focus:outline-slate-500"
              />
              <textarea 
                placeholder="Contents / specs details..." value={newHistory.content}
                onChange={e => setNewHistory({...newHistory, content: e.target.value})}
                className="w-full text-xs p-2 rounded-lg border border-gray-200 focus:outline-slate-500 h-16 resize-none"
              />
              <button 
                onClick={() => handleAddItem('vaultHistory', newHistory, () => setNewHistory({title:'', content:''}))}
                className="w-full text-[11px] bg-slate-700 text-white font-bold py-1.5 rounded-lg hover:bg-slate-800 transition"
              >
                + Add Document
              </button>
            </div>

            <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-4">Specifications & Lists</h4>
            <div className="space-y-4 max-h-[410px] overflow-y-auto pr-1">
              {historyItems.map((item) => {
                const isEditing = editingId === item.id;
                return (
                  <div key={item.id} className="p-4 bg-slate-50 rounded-xl border border-slate-200 relative group">
                    {isEditing ? (
                      <div className="space-y-2">
                        <input type="text" value={editFields.title} onChange={e => setEditFields({...editFields, title: e.target.value})} className="w-full text-xs p-1.5 rounded border" />
                        <textarea value={editFields.content} onChange={e => setEditFields({...editFields, content: e.target.value})} className="w-full text-xs p-1.5 rounded border h-16" />
                        <div className="flex gap-2">
                          <button onClick={() => handleSaveEdit('vaultHistory', item.id)} className="text-[10px] bg-green-600 text-white px-2 py-1 rounded font-bold">Save</button>
                          <button onClick={() => setEditingId(null)} className="text-[10px] bg-gray-400 text-white px-2 py-1 rounded font-bold">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 bg-white/80 backdrop-blur-sm p-1 rounded-lg border">
                          <button onClick={() => startEditing(item, { content: item.content })} className="text-[10px] text-gray-500 hover:text-blue-600 px-1">Edit</button>
                          <button onClick={() => handleDeleteItem('vaultHistory', item.id)} className="text-[10px] text-gray-500 hover:text-red-600 px-1">Delete</button>
                        </div>
                        <p className="font-bold text-slate-900 text-sm leading-tight pr-12">{item.title}</p>
                        <p className="text-xs text-slate-500 leading-relaxed font-mono bg-white p-2.5 rounded-lg border border-slate-100 mt-2 shadow-inner">
                          {item.content}
                        </p>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default VaultMatrix;